import {
  bySubstance,
  countLabel,
  hero,
  heroCss,
  joinLabels,
  panel,
  panelCss,
  positive,
} from '../armKit';
import { bookmarkCounts, mentionCounts } from '../batchFetchers';
import {
  analyticsDashboardUrl,
  upgradeUrl,
  workEditUrl,
} from '../destinations';
import { viewedUser, viewerIsPremium } from '../profileFacts';

import type { HeroAction } from '../armKit';
import type { ArmContext, ArmSpec } from '../armRegistry';
import type { WorkFacts } from '../pageData';
import type { ProfileCard } from '../surface';

type Facts = Map<number, WorkFacts>;
type Hero = {
  card: ProfileCard;
  url: string;
  views: number | null;
  label: string;
};

const FLAG = 'paperfirst';
const HERO = 'hero';

const MOST_READ = 'Your most-read paper';
const MOST_SUBSTANTIAL = 'Your most substantial paper';
const READ = 'Read it';
const EDIT = 'Edit it';
const READERS = 'See who read it';

const READ_SOURCE = 'profile-liautotest-hero-read';
const EDIT_SOURCE = 'profile-liautotest-hero-edit';
const READERS_SOURCE = 'profile-liautotest-hero-readers';

function pagesOf(facts: Facts, workId: number): number | null | undefined {
  return facts.get(workId)?.pageCount;
}

// The endpoint only carries the all-time figure, so all-time is what the label
// says: a 30-day count is not reachable from this page.
function countsText(
  views: number | null,
  bookmarks?: number,
  mentions?: number,
): string {
  return joinLabels([
    countLabel(positive(views), 'all-time view'),
    countLabel(positive(bookmarks), 'bookmark'),
    countLabel(positive(mentions), 'mention'),
  ]);
}

function mostRead(cards: ProfileCard[], facts: Facts): Hero | null {
  const ranked = cards
    .flatMap((card) => {
      const views = positive(facts.get(card.workId)?.views);
      if (card.url == null || views == null) return [];

      return [{ card, url: card.url, views, label: MOST_READ }];
    })
    .sort((a, b) => b.views - a.views);

  return ranked[0] ?? null;
}

function mostSubstantial(cards: ProfileCard[], facts: Facts): Hero | null {
  const card = cards
    .filter(
      (candidate) =>
        candidate.url != null &&
        positive(pagesOf(facts, candidate.workId)) != null,
    )
    .sort(bySubstance((workId) => pagesOf(facts, workId)))[0];

  return card?.url == null
    ? null
    : { card, url: card.url, views: null, label: MOST_SUBSTANTIAL };
}

function readersUrl(): string | null {
  if (!viewerIsPremium()) return upgradeUrl('readers', READERS_SOURCE);

  const user = viewedUser();
  const pageName = user?.page_name;
  if (pageName == null || pageName === '') return null;

  return analyticsDashboardUrl(pageName, 'readers', user?.domain_name);
}

function actionsFor(pick: Hero, ctx: ArmContext): HeroAction[] {
  const { workId } = pick.card;
  const readers = readersUrl();

  return [
    {
      label: READ,
      href: pick.url,
      onClick: () => ctx.track(READ_SOURCE, { entityId: workId }),
    },
    {
      label: EDIT,
      href: workEditUrl(workId),
      onClick: () => ctx.track(EDIT_SOURCE, { entityId: workId }),
    },
    ...(readers == null
      ? []
      : [
          {
            label: READERS,
            href: readers,
            onClick: () => ctx.track(READERS_SOURCE, { entityId: workId }),
          },
        ]),
  ];
}

function heroPanel(
  pick: Hero,
  ctx: ArmContext,
): { root: HTMLElement; setMeta: (text: string) => void } {
  const { workId } = pick.card;
  const { root, body } = panel({ title: pick.label });

  const { setMeta } = hero(body, {
    title: pick.card.title,
    href: pick.url,
    onTitleClick: () => ctx.track(READ_SOURCE, { entityId: workId }),
    meta: countsText(pick.views),
    abstract: pick.card.abstractText(),
    actions: actionsFor(pick, ctx),
  });

  return { root, setMeta };
}

export const paperPageFirst: ArmSpec = {
  onReady: async (ctx) => {
    const facts = await ctx.workFacts();
    const cards = ctx.cards();
    const pick = mostRead(cards, facts) ?? mostSubstantial(cards, facts);
    if (pick == null) return;

    // Last gate before the first mutation: no list means nowhere to mount, and
    // an unmounted hero is worse than control.
    const slot = ctx.mountSlot('listHero');
    if (slot == null) return;

    ctx.injectStyles(
      panelCss(ctx.scope) +
        heroCss(ctx.scope, {
          abstractLineHeight: '1.6',
          abstractMarginBottom: '16px',
        }),
    );

    const { root, setMeta } = heroPanel(pick, ctx);
    pick.card.hide();
    pick.card.setFlag(FLAG, HERO);
    slot.appendChild(root);

    // One work id per signal, and additive: the hero is already whole, so a
    // batch that never answers leaves it reading exactly as it rendered.
    const [bookmarks, mentions] = await Promise.all([
      bookmarkCounts([pick.card.workId]),
      mentionCounts([pick.card.workId]),
    ]);

    setMeta(
      countsText(
        pick.views,
        bookmarks.get(pick.card.workId),
        mentions.get(pick.card.workId),
      ),
    );
  },
};
